import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatEatDateTime } from '@wodi/domain';
import type {
  FacilityRegistryStatus,
  RegistryFacilityView,
  RegistryPractitionerView,
  ShaSettingsView,
  ShaSetupView,
} from '@wodi/shared';
import { Button, LoadingBlock, Notice, RadioGroup, Select, Tag, TextField } from '@wodi/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import {
  activatePreauthList,
  linkFacilityToRegistry,
  linkPractitionerToRegistry,
  refreshFacilityLink,
  searchFacilityRegistry,
  searchPractitionerRegistry,
  shaKeys,
  shaSettingsQuery,
  shaSetupQuery,
  staffQuery,
  updateShaSettings,
} from '../api/sha.ts';
import billing from '../billing/billing.module.css';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../sha/sha.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > SHA (docs/plans/phase1-sha.md §9.6): the connection, the facility's link to
// the national Facility Registry with the comparison in words, practitioners' links to the
// Health Worker Registry, and how claims and eligibility behave here.

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className={billing.section} aria-labelledby={id}>
      <h2 id={id} className={billing.sectionHeading}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function ConnectionSection({ setup }: { setup: ShaSetupView }) {
  return (
    <Section id="sha-connection" title="Connection">
      {setup.connection === 'test' ? (
        <p>
          <Tag state="pending">Test connection (synthetic data)</Tag> Wodi is using synthetic SHA
          services. Nothing here reaches SHA.
        </p>
      ) : (
        <p>
          <Tag state="attention">Not connected</Tag> SHA connection not set up yet. Reception can
          record checks made on the SHA portal by hand; claims are prepared and held until the
          connection exists.
        </p>
      )}
    </Section>
  );
}

function FacilityResult({
  result,
  onLink,
  pending,
}: {
  result: RegistryFacilityView;
  onLink: () => void;
  pending: boolean;
}) {
  return (
    <tr>
      <td>{result.name}</td>
      <td className={styles.id}>{result.mflCode ?? 'None'}</td>
      <td>{result.levelDisplay ?? 'Not given'}</td>
      <td>{[result.county, result.subCounty].filter((v) => v !== null).join(', ')}</td>
      <td>
        <Button dense onPress={onLink} isPending={pending} pendingLabel="Linking…">
          Link this record
        </Button>
      </td>
    </tr>
  );
}

function FacilitySection({
  facility,
  canLink,
}: {
  facility: FacilityRegistryStatus;
  canLink: boolean;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [mflCode, setMflCode] = useState(facility.mflCode ?? '');
  const [name, setName] = useState('');
  const search = useMutation({
    mutationFn: () =>
      searchFacilityRegistry({
        ...(mflCode.trim() === '' ? {} : { mflCode: mflCode.trim() }),
        ...(name.trim() === '' ? {} : { name: name.trim() }),
      }),
  });
  const onSetup = async (view: ShaSetupView) => {
    queryClient.setQueryData(shaKeys.setup, view);
    toasts.show({ title: 'Facility registry link saved' });
    search.reset();
  };
  const link = useMutation({
    mutationFn: (registryId: string) => linkFacilityToRegistry(facility.facilityId, registryId),
    onSuccess: onSetup,
  });
  const refresh = useMutation({
    mutationFn: () => refreshFacilityLink(facility.facilityId),
    onSuccess: onSetup,
  });
  const r = facility.registry;
  return (
    <Section id="sha-facility" title="Facility">
      <p>
        <Tag state={facility.check === 'matches' ? 'done' : 'attention'}>
          {facility.check === 'matches'
            ? 'Matches'
            : facility.check === 'not_linked'
              ? 'Not linked'
              : 'Needs attention'}
        </Tag>{' '}
        {facility.words}
      </p>
      <table className={styles.table} aria-label="This facility and its registry record">
        <thead>
          <tr>
            <th scope="col">Detail</th>
            <th scope="col">Here</th>
            <th scope="col">National registry</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Name</th>
            <td>{facility.name}</td>
            <td>{r?.name ?? 'Not linked'}</td>
          </tr>
          <tr>
            <th scope="row">MFL code</th>
            <td className={styles.id}>{facility.mflCode ?? 'None'}</td>
            <td className={styles.id}>{r === null ? 'Not linked' : (r.mflCode ?? 'None')}</td>
          </tr>
          <tr>
            <th scope="row">Level</th>
            <td>{`Level ${facility.level}`}</td>
            <td>{r === null ? 'Not linked' : (r.levelDisplay ?? 'Not given')}</td>
          </tr>
          {r === null ? null : (
            <tr>
              <th scope="row">Registry ID</th>
              <td />
              <td>
                <span className={styles.id}>{r.registryId}</span>
                <span className={styles.detail}>
                  {' '}
                  fetched {formatEatDateTime(new Date(r.fetchedAt))}
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {canLink ? (
        <>
          {r === null ? null : (
            <div className={styles.actions}>
              <Button
                isDisabled={!online}
                isPending={refresh.isPending}
                pendingLabel="Refreshing…"
                onPress={() => {
                  refresh.mutate();
                }}
              >
                Refresh from registry
              </Button>
            </div>
          )}
          <form
            className={styles.form}
            aria-label="Search the national Facility Registry"
            onSubmit={(event) => {
              event.preventDefault();
              if (mflCode.trim() !== '' || name.trim().length >= 2) search.mutate();
            }}
          >
            <TextField label="MFL code" value={mflCode} onChange={setMflCode} />
            <TextField
              label="Facility name"
              description="Part of the name is enough."
              value={name}
              onChange={setName}
            />
            <div className={styles.actions}>
              <Button
                type="submit"
                isDisabled={!online || (mflCode.trim() === '' && name.trim().length < 2)}
                isPending={search.isPending}
                pendingLabel="Searching…"
              >
                Search national registry
              </Button>
            </div>
          </form>
          {!online ? (
            <Notice tone="info">Searching the national registry needs a connection.</Notice>
          ) : null}
          {[search, link, refresh].map((m, i) =>
            m.isError ? (
              <Notice key={i} tone="error">
                {errorText(m.error, "The national registry couldn't be reached. Try again.")}
              </Notice>
            ) : null,
          )}
          {search.isSuccess && search.data.items.length === 0 ? (
            <Notice tone="info">
              No facility in the national registry matches. Check the MFL code.
            </Notice>
          ) : null}
          {search.isSuccess && search.data.items.length > 0 ? (
            <table className={styles.table} aria-label="Registry results">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">MFL code</th>
                  <th scope="col">Level</th>
                  <th scope="col">Location</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {search.data.items.map((result) => (
                  <FacilityResult
                    key={result.registryId}
                    result={result}
                    pending={link.isPending && link.variables === result.registryId}
                    onLink={() => {
                      link.mutate(result.registryId);
                    }}
                  />
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      ) : null}
    </Section>
  );
}

function PractitionerLinker() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const staff = useQuery(staffQuery);
  const [userId, setUserId] = useState<string | null>(null);
  const [kind, setKind] = useState<'registration_number' | 'national_id'>('registration_number');
  const [value, setValue] = useState('');
  const search = useMutation({
    mutationFn: () => searchPractitionerRegistry({ kind, value: value.trim() }),
  });
  const link = useMutation({
    mutationFn: (registryId: string) => linkPractitionerToRegistry(userId ?? '', registryId),
    onSuccess: (view) => {
      queryClient.setQueryData(shaKeys.setup, view);
      toasts.show({ title: 'Practitioner registry link saved' });
      search.reset();
      setValue('');
    },
  });
  if (staff.isPending) return <LoadingBlock label="Loading staff" rows={1} />;
  if (staff.isError)
    return <Notice tone="error">{errorText(staff.error, "Staff couldn't be loaded.")}</Notice>;
  return (
    <form
      className={styles.form}
      aria-label="Link a practitioner to the national Health Worker Registry"
      onSubmit={(event) => {
        event.preventDefault();
        if (userId !== null && value.trim().length >= 3) search.mutate();
      }}
    >
      <Select
        label="Staff member"
        placeholder="Choose a person"
        options={staff.data.items
          .filter((u) => u.status === 'active')
          .map((u) => ({ id: u.id, label: u.displayName }))}
        value={userId}
        onChange={(key) => {
          setUserId(key === null ? null : String(key));
        }}
      />
      <RadioGroup
        label="Search by"
        orientation="horizontal"
        value={kind}
        onChange={(v) => {
          setKind(v === 'national_id' ? 'national_id' : 'registration_number');
        }}
        options={[
          { id: 'registration_number', label: 'Registration number' },
          { id: 'national_id', label: 'ID number' },
        ]}
      />
      <TextField
        label={kind === 'national_id' ? 'ID number' : 'Registration number'}
        value={value}
        onChange={setValue}
      />
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || userId === null || value.trim().length < 3}
          isPending={search.isPending}
          pendingLabel="Searching…"
        >
          Search national registry
        </Button>
      </div>
      {search.isError || link.isError ? (
        <Notice tone="error">
          {errorText(
            search.error ?? link.error,
            "The national registry couldn't be reached. Try again.",
          )}
        </Notice>
      ) : null}
      {search.isSuccess && search.data.items.length === 0 ? (
        <Notice tone="info">Nobody in the national registry matches. Check the number.</Notice>
      ) : null}
      {search.isSuccess && search.data.items.length > 0 ? (
        <ul className={styles.schemes} aria-label="Registry results">
          {search.data.items.map((p: RegistryPractitionerView) => (
            <li key={p.registryId} className={styles.scheme}>
              <span className={styles.schemeLine}>{p.names}</span>
              <span className={styles.detail}>
                {[p.cadre, p.council].filter((v) => v !== null).join(', ')}
              </span>
              <span className={styles.detail}>
                Licence <span className={styles.id}>{p.licenceNumber ?? 'not given'}</span>, valid
                to {p.licenceValidTo ?? 'not given'}
              </span>
              <Button
                dense
                isPending={link.isPending}
                pendingLabel="Linking…"
                onPress={() => {
                  link.mutate(p.registryId);
                }}
              >
                Link this record
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}

function PractitionersSection({ setup, canLink }: { setup: ShaSetupView; canLink: boolean }) {
  return (
    <Section id="sha-practitioners" title="Practitioners">
      <p className={styles.detail}>
        SHA claims name the practitioners who ordered and performed each service, from their
        national registry records. A claim waits while a practitioner isn&apos;t linked or their
        licence has expired. Their cadre, local licence number and expiry are kept in{' '}
        <Link to="/settings/practitioners">Settings, Practitioners</Link>.
      </p>
      {setup.practitioners.length === 0 ? (
        <p>No practitioners are linked to the national Health Worker Registry yet.</p>
      ) : (
        <table className={styles.table} aria-label="Linked practitioners">
          <thead>
            <tr>
              <th scope="col">Staff member</th>
              <th scope="col">Registry record</th>
              <th scope="col">Licence valid to</th>
              <th scope="col">State</th>
            </tr>
          </thead>
          <tbody>
            {setup.practitioners.map((p) => (
              <tr key={p.userId}>
                <td>{p.displayName}</td>
                <td>
                  {p.registry.names} <span className={styles.id}>{p.registry.registryId}</span>
                </td>
                <td>{p.registry.licenceValidTo ?? 'Not given'}</td>
                <td>
                  <Tag state={p.check === 'matches' ? 'done' : 'attention'}>{p.words}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canLink ? <PractitionerLinker /> : null}
    </Section>
  );
}

function PreauthListSection({ setup, canManage }: { setup: ShaSetupView; canManage: boolean }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const activate = useMutation({
    mutationFn: (id: string) => activatePreauthList(id),
    onSuccess: (view) => {
      queryClient.setQueryData(shaKeys.setup, view);
      toasts.show({ title: 'Pre-authorisation list activated' });
    },
  });
  const active = setup.preauthLists.find((l) => l.status === 'active');
  return (
    <Section id="sha-preauth" title="Pre-authorisation list">
      {active === undefined ? (
        <p>
          <Tag state="attention">Not loaded</Tag> Pre-authorisation list not loaded. Care isn&apos;t
          held back, but claims for services that might need SHA approval wait until a list is
          active. Load the list from SHA&apos;s official benefit package.
        </p>
      ) : (
        <p>
          <Tag state="done">Active</Tag>
          {` ${active.sourceReference}: ${String(active.requirementCount)} services need SHA approval.`}
        </p>
      )}
      {setup.preauthLists.length > 0 ? (
        <table className={styles.table} aria-label="Pre-authorisation list versions">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col" className={styles.number}>
                Services
              </th>
              <th scope="col">Loaded</th>
              <th scope="col">State</th>
            </tr>
          </thead>
          <tbody>
            {setup.preauthLists.map((l) => (
              <tr key={l.id}>
                <td>{l.sourceReference}</td>
                <td className={styles.number}>{l.requirementCount}</td>
                <td>{formatEatDateTime(new Date(l.createdAt))}</td>
                <td>
                  {l.status === 'ready' && canManage ? (
                    <Button
                      dense
                      isDisabled={!online}
                      isPending={activate.isPending && activate.variables === l.id}
                      pendingLabel="Activating…"
                      onPress={() => {
                        activate.mutate(l.id);
                      }}
                    >
                      Activate
                    </Button>
                  ) : (
                    { importing: 'Loading', ready: 'Ready', active: 'Active', retired: 'Retired' }[
                      l.status
                    ]
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {activate.isError ? (
        <Notice tone="error">{errorText(activate.error, "The list couldn't be activated.")}</Notice>
      ) : null}
      {canManage ? (
        <p>
          <Link to="/settings/imports">Import list</Link>
        </p>
      ) : null}
    </Section>
  );
}

function CodesSection({ setup }: { setup: ShaSetupView }) {
  return (
    <Section id="sha-codes" title="SHA codes">
      {setup.itemsWithoutShaCode === 0 ? (
        <p>Every service and item priced for SHA has an SHA intervention code.</p>
      ) : (
        <p>
          {`${String(setup.itemsWithoutShaCode)} services and items priced for SHA have no SHA intervention code, so they can't go on a claim. `}
          <Link to="/settings/services">Add their codes in Services and prices</Link>
        </p>
      )}
    </Section>
  );
}

function ClaimsSection({ settings }: { settings: ShaSettingsView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [trigger, setTrigger] = useState(settings.claimTrigger);
  const [hours, setHours] = useState(String(Math.round(settings.eligibilityMaxAgeMinutes / 60)));
  const [provider, setProvider] = useState(settings.providerIdentifier ?? '');
  const hoursNumber = Number(hours);
  const hoursValid = Number.isInteger(hoursNumber) && hoursNumber >= 1 && hoursNumber <= 168;
  const save = useMutation({
    mutationFn: () =>
      updateShaSettings({
        expectedVersion: settings.version,
        claimTrigger: trigger,
        eligibilityMaxAgeMinutes: hoursNumber * 60,
        providerIdentifier: provider.trim() === '' ? null : provider.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shaKeys.settings });
      toasts.show({ title: 'SHA settings saved' });
    },
  });
  return (
    <Section id="sha-claims" title="Claims and eligibility">
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (hoursValid) save.mutate();
        }}
      >
        <RadioGroup
          label="Send claims"
          value={trigger}
          onChange={(v) => {
            setTrigger(v === 'manual' ? 'manual' : 'on_visit_close');
          }}
          options={[
            {
              id: 'on_visit_close',
              label: 'When the visit closes',
              description:
                'Built and checked at close, and sent when ready. Problems go to the claims queue.',
            },
            { id: 'manual', label: 'When a claims officer sends them' },
          ]}
        />
        <TextField
          label="An SHA check stays current for"
          unit="hours"
          inputMode="numeric"
          value={hours}
          onChange={setHours}
          {...(hoursValid ? {} : { errorMessage: 'Enter a whole number of hours from 1 to 168.' })}
        />
        <TextField
          label="SHA provider identifier"
          description="As SHA issued it to this facility. Leave empty until SHA confirms it."
          value={provider}
          onChange={setProvider}
        />
        {save.isError ? (
          <Notice tone="error">
            {errorText(save.error, "The settings couldn't be saved. Try again.")}
          </Notice>
        ) : null}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online || !hoursValid}
            isPending={save.isPending}
            pendingLabel="Saving…"
          >
            Save SHA settings
          </Button>
        </div>
      </form>
    </Section>
  );
}

export function ShaSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const setup = useQuery(shaSetupQuery);
  const settings = useQuery(shaSettingsQuery);
  const canLink =
    session !== null && can(session, 'registries.link') && can(session, 'registries.search');
  const problem = setup.error ?? settings.error;
  return (
    <SettingsPage title="SHA">
      {setup.isPending || settings.isPending ? (
        <LoadingBlock label="Loading SHA settings" rows={4} />
      ) : null}
      {problem !== null ? (
        <Notice
          tone="error"
          action={
            <Button
              dense
              onPress={() => {
                void setup.refetch();
                void settings.refetch();
              }}
            >
              Try again
            </Button>
          }
        >
          {errorText(problem, "SHA settings couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {setup.isSuccess ? (
        <>
          <ConnectionSection setup={setup.data} />
          <FacilitySection facility={setup.data.facility} canLink={canLink} />
          <PractitionersSection setup={setup.data} canLink={canLink} />
          <PreauthListSection
            setup={setup.data}
            canManage={session !== null && can(session, 'preauth_list.manage')}
          />
          <CodesSection setup={setup.data} />
        </>
      ) : null}
      {settings.isSuccess ? (
        <ClaimsSection key={settings.data.version} settings={settings.data} />
      ) : null}
    </SettingsPage>
  );
}
